import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { installChunkReloadHandler } from './lib/lazyWithReload';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import Konva from 'konva';
import { AuthProvider } from './lib/auth';
import { ThemeProvider } from './lib/theme';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/ui/Toast';
import { UpdatePrompt } from './components/UpdatePrompt';
import { App } from './App';
import './fonts.css';
import './index.css';

// v0.11.18: Konva multi-touch fix. Bez tej flagi `touchmove` nie firuje na
// stage'u podczas Konva-internal drag (z pierwszego palca) — drugi palec
// przy pinch zoom nie aktualizuje touchmove → pinch handler nie widzi
// kolejnych klatek → anchor point dryfuje, zoom „skacze".
// Per Konva oficjalna dokumentacja Multi-touch Scale Stage.
Konva.hitOnDragEnabled = true;

// Initialize Sentry — only in production or when DSN is set.
// v0.10.11: hardening pod migrację Sentry → GlitchTip. GlitchTip ignoruje
// payload'y dla features których nie wspiera (transactions, sessions, profiling).
// Wyłączamy je żeby nie marnować transferu. Pełna analiza:
// docs/monitoring/INSPECTOR-GLITCHTIP-MIGRATION.md
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    // Format `inspector-web@version+commit` MUSI pasować do `release.name`
    // w `@sentry/vite-plugin` (vite.config.ts) żeby source maps sklejały się
    // ze stack traces z konkretnych buildów.
    release: `inspector-web@${__APP_VERSION__}+${__APP_COMMIT__}`,
    // GlitchTip: tracing nie wspierane → 0 (Sentry SDK 10 nie wyśle transactions).
    // Sessions w SDK 10 są kontrolowane przez browserSessionIntegration; GlitchTip
    // pochłania session events bez efektu — strata transferu pomijalna (2 events
    // per session vs setki errors). Bez explicit autoSessionTracking (deprecated).
    tracesSampleRate: 0,
    // GDPR — domyślnie nie wysyłaj IP, headerów, cookies.
    sendDefaultPii: false,
    // Browser extensions injectują kod w global scope — typowy szum mobile.
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
    ],
    // Filtruj typowe non-actionable errors (network glitches, ResizeObserver,
    // promise rejections z anulowanych fetch'y).
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      'NetworkError when attempting to fetch resource',
      'AbortError',
      /Failed to fetch/,
      /Load failed/,
    ],
    // Brak `browserTracingIntegration` — GlitchTip ignoruje transaction events.
  });
}

// v0.123.0 — nieudany preload kawałka po wdrożeniu odświeża stronę zamiast
// czekać, aż użytkownik wejdzie w sekcję i zobaczy „Coś poszło nie tak".
installChunkReloadHandler();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <HelmetProvider>
      <ErrorBoundary>
        <ThemeProvider>
          <BrowserRouter>
            <ToastProvider>
              <AuthProvider>
                <App />
                <UpdatePrompt />
              </AuthProvider>
            </ToastProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </HelmetProvider>
  </StrictMode>,
);

// Register Service Worker with update detection
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const reg = await navigator.serviceWorker.register('/sw.js');

      // Check for updates periodically (every 60 min)
      setInterval(() => reg.update(), 60 * 60 * 1000);

      // Detect waiting worker (new version available) — emit event obsłużony
      // przez <UpdatePrompt> (baner z buttonami "Odśwież teraz" / "Później")
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            window.dispatchEvent(
              new CustomEvent('sw-update-available', { detail: newWorker }),
            );
          }
        });
      });
    } catch {
      // SW registration failed — app works without it
    }
  });
}
